import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Database, Calendar, Sparkles, Download, Upload, FileSpreadsheet, AlertTriangle, HardDriveDownload, Users as UsersIcon, RefreshCw, Store, Tag, Plus, Pencil, Trash2, X, MailCheck, MailWarning } from "lucide-react";
import { fetchRequirements, setRequirements } from "../lib/requirements";

function downloadBlob(res, fallbackName) {
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const CSV_FIELDS = [
  ["wati_leads_csv", "Leads CSV (TeleCRM)", "Incoming leads — single source of truth (phone, name, lead_stage, lead_owner, created_at...)"],
  ["jordyblue_reference_csv", "JordyBlue Reference Sheet", "Read-only cross-reference. Any UNWIND lead phone found here is tagged 'Also in JordyBlue'."],
  ["wati_updated_csv", "Updated Contacts Sheet (optional)", "Optional second sheet to reconcile WATI stage/owner changes. Leave blank to disable — TeleCRM now writes back to the main leads sheet."],
  ["deal_closing_csv", "Deal Closing CSV", "Closed deals (Phone Number, Number in Clappia, Sale Value, Closed by)"],
  ["order_summary_csv", "Order Summary CSV", "Orders (Order ID, Total Order Value, Phone Number, Products)"],
  ["order_detail_csv", "Order Detail CSV", "Line items / dates (Order ID, Date of Placing Order)"],
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  // ---- Email delivery health (Resend / OTP) ----
  const [emailHealth, setEmailHealth] = useState(null);
  const loadEmailHealth = () => {
    api.get("/admin/email-health").then((r) => setEmailHealth(r.data)).catch(() => setEmailHealth(null));
  };
  useEffect(() => { loadEmailHealth(); }, []);

  // ---- Stores management (multi-tenancy) ----
  const [stores, setStores] = useState([]);
  const [newStoreName, setNewStoreName] = useState("");
  const [storeBusy, setStoreBusy] = useState(false);
  const loadStores = () => api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
  useEffect(() => { loadStores(); }, []);

  const addStore = async (e) => {
    e.preventDefault();
    const name = newStoreName.trim();
    if (!name) return;
    setStoreBusy(true);
    try {
      await api.post("/stores", { name });
      toast.success(`Added store: ${name}`);
      setNewStoreName("");
      loadStores();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not add store");
    } finally {
      setStoreBusy(false);
    }
  };

  const removeStore = async (st) => {
    if (!window.confirm(`Delete store "${st.name}"? This is only allowed when no users/leads are still attached to it.`)) return;
    try {
      await api.delete(`/stores/${st.id}`);
      toast.success(`Deleted ${st.name}`);
      loadStores();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not delete store");
    }
  };

  // ---- Requirements (Sofa / Bed / Recliner …) — admin-configurable ----
  const [reqs, setReqs] = useState([]);
  const [reqCounts, setReqCounts] = useState({});
  const [newReq, setNewReq] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [editingReq, setEditingReq] = useState(null);   // { old, new }
  const [deletingReq, setDeletingReq] = useState(null); // { name, count, migrate_to }

  const loadReqs = () => {
    api.get("/requirements").then((r) => {
      setReqs(r.data.requirements || []);
      setReqCounts(r.data.counts || {});
      setRequirements(r.data.requirements || []);  // broadcast to other consumers
    }).catch(() => {});
  };
  useEffect(() => { loadReqs(); }, []);

  const addReq = async () => {
    const name = newReq.trim();
    if (!name) return;
    setReqBusy(true);
    try {
      const { data } = await api.post("/requirements", { name });
      setRequirements(data.requirements);
      setNewReq("");
      loadReqs();
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add requirement");
    } finally { setReqBusy(false); }
  };

  const saveRename = async () => {
    if (!editingReq) return;
    const next = (editingReq.new || "").trim();
    if (!next || next === editingReq.old) { setEditingReq(null); return; }
    setReqBusy(true);
    try {
      const { data } = await api.patch("/requirements", { old: editingReq.old, new: next });
      setRequirements(data.requirements);
      setEditingReq(null);
      loadReqs();
      toast.success(`Renamed — ${data.migrated} lead(s) updated`);
      fetchRequirements(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not rename");
    } finally { setReqBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deletingReq) return;
    setReqBusy(true);
    try {
      const body = { name: deletingReq.name, migrate_to: deletingReq.migrate_to || null };
      const { data } = await api.delete("/requirements", { data: body });
      setRequirements(data.requirements);
      setDeletingReq(null);
      loadReqs();
      const tgt = data.migrated_to ? `"${data.migrated_to}"` : "unset";
      toast.success(`Deleted — ${data.migrated} lead(s) migrated to ${tgt}`);
      fetchRequirements(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete");
    } finally { setReqBusy(false); }
  };

  // ---- Bulk store reassignment for non-Fresh leads (one-click migration) ----
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);   // {total_non_fresh, already_on_target, would_migrate}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const bulkPreviewRun = async () => {
    if (!bulkTarget) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/reassign-store-non-fresh",
        { target_store: bulkTarget, dry_run: true });
      setBulkPreview(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Preview failed");
      setBulkPreview(null);
    } finally { setBulkBusy(false); }
  };

  const bulkApply = async () => {
    if (!bulkTarget) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/reassign-store-non-fresh",
        { target_store: bulkTarget, dry_run: false });
      toast.success(`Migrated ${data.migrated} lead(s) to "${bulkTarget}".`);
      setBulkPreview(data);
      setBulkConfirmOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Migration failed");
    } finally { setBulkBusy(false); }
  };

  // ---- WATI owner -> CRM user mapping ----
  const [watiOwners, setWatiOwners] = useState([]);
  const [watiUsers, setWatiUsers] = useState([]);
  const [ownerDraft, setOwnerDraft] = useState({});
  const [applyingMap, setApplyingMap] = useState(false);

  const loadWatiOwners = () => {
    api.get("/wati-owners").then((r) => {
      setWatiOwners(r.data.owners);
      setWatiUsers(r.data.users);
      const d = {};
      r.data.owners.forEach((o) => { d[o.wati_owner] = o.mapped_user_id || ""; });
      setOwnerDraft(d);
    }).catch(() => {});
  };
  useEffect(() => { loadWatiOwners(); }, []);

  const applyOwnerMap = async () => {
    setApplyingMap(true);
    try {
      const { data } = await api.put("/wati-owner-map", { map: ownerDraft });
      toast.success(`Mapping saved — ${data.assigned} lead(s) assigned to their WATI owner.`);
      loadWatiOwners();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save mapping");
    } finally {
      setApplyingMap(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const [clearingWati, setClearingWati] = useState(false);
  const clearWatiTags = async () => {
    if (!window.confirm(
      "Wipe `wati_lead_stage` and `wati_owner` from ALL leads?\n\n" +
      "Use this after disabling the Updated Contacts sheet to clean up stale stage/owner labels (e.g. \"WATI: 2- Photos sent\") inherited from a previous CRM.\n\nProceed?")) return;
    setClearingWati(true);
    try {
      const { data } = await api.post("/admin/clear-wati-tags");
      toast.success(`Cleared WATI tags on ${data.cleared} lead(s).`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not clear WATI tags");
    } finally {
      setClearingWati(false);
    }
  };

  const [reconciling, setReconciling] = useState(false);
  const runReco = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/reconcile");
      if (data.error) toast.warning(data.error);
      else toast.success(`Reconciliation done — ${data.matched} lead(s) matched & marked Deal Won.`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setReconciling(false);
    }
  };

  const [rerunning, setRerunning] = useState(false);
  const wipeRerun = async () => {
    if (!window.confirm(
      "Wipe ALL AI enrichment data and re-run every lead from scratch with the latest logic?\n\n" +
      "• Manually-confirmed leads are kept.\n" +
      "• Runs in the background and uses your Gemini API quota.\n\nProceed?")) return;
    setRerunning(true);
    try {
      const { data } = await api.post("/enrich/wipe-rerun");
      toast.success(`Wiped & re-running ${data.queued} lead(s); kept ${data.preserved} manually-confirmed. This completes in the background.`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start re-run");
    } finally {
      setRerunning(false);
    }
  };

  // ---- Full backup / restore ----
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadFullBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/export/backup.xlsx", { responseType: "blob" });
      downloadBlob(res, "unwind-full-backup.xlsx");
      toast.success("Full backup downloaded");
    } catch (e) {
      toast.error("Backup failed");
    } finally {
      setDownloading(false);
    }
  };

  const doRestore = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/import/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restored ${data.leads} leads, ${data.users} users. Reloading…`);
      setRestoreFile(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template.xlsx", { responseType: "blob" });
      downloadBlob(res, "unwind-lead-import-template.xlsx");
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.created} new lead(s); skipped ${data.skipped_duplicates} duplicate(s).`);
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (!s) return <div className="flex-1 flex items-center justify-center text-stone-400">Loading…</div>;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center justify-between border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Settings</h1>
          <p className="text-xs text-stone-500">Data sources & intake configuration</p>
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="settings-save"
          className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
        </button>
      </div>

      <div className="p-6 max-w-3xl space-y-6">
        <EmailHealthCard health={emailHealth} onRefresh={loadEmailHealth} />

        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Database className="h-4 w-4 text-stone-500" />
            <p className="text-sm font-semibold text-stone-800">Google Sheet CSV sources</p>
          </div>
          <p className="text-xs text-stone-400 mb-4">Published CSV URLs. The CRM only reads these — it never writes back.</p>
          <div className="space-y-4">
            {CSV_FIELDS.map(([k, label, hint]) => (
              <div key={k}>
                <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">{label}</label>
                <p className="text-xs text-stone-400 mb-1">{hint}</p>
                <input
                  value={s[k] || ""}
                  onChange={(e) => setS((p) => ({ ...p, [k]: e.target.value }))}
                  placeholder="https://docs.google.com/.../pub?output=csv"
                  data-testid={`settings-${k}`}
                  className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-stone-500" />
            <p className="text-sm font-semibold text-stone-800">Intake rules</p>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">Lead start date (cutoff)</label>
            <p className="text-xs text-stone-400 mb-1">Only ingest leads created on/after this date.</p>
            <input
              type="date"
              value={(s.lead_start_date || "").slice(0, 10)}
              onChange={(e) => setS((p) => ({ ...p, lead_start_date: e.target.value }))}
              data-testid="settings-lead-start-date"
              className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60"
            />
          </div>
          <p className="text-xs text-stone-400">Automatic intake runs every 15 minutes. Use "Pull now" on the pipeline for an immediate sync.</p>
          <div className="border-t border-stone-100 pt-4">
            <p className="text-sm font-semibold text-stone-800 mb-1">Clear WATI tags</p>
            <p className="text-xs text-stone-400 mb-3">Wipes <code>wati_lead_stage</code> &amp; <code>wati_owner</code> from every lead. Run once after disabling the Updated Contacts sheet to remove inherited stage/owner labels from a previous CRM.</p>
            <button onClick={clearWatiTags} disabled={clearingWati} data-testid="settings-clear-wati"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              {clearingWati ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear all WATI stages & owners"}
            </button>
          </div>
          <div className="border-t border-stone-100 pt-4">
            <p className="text-sm font-semibold text-stone-800 mb-1">Deal reconciliation</p>
            <p className="text-xs text-stone-400 mb-3">Matches WATI leads against the Deal Closing sheet (Phone Number or Number in Clappia) and marks matches as Won. Runs automatically every night at 00:05 IST.</p>
            <button onClick={runReco} disabled={reconciling} data-testid="settings-reconcile"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              {reconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run reconciliation now"}
            </button>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              <div>
                <p className="text-sm font-semibold text-stone-800">Automated AI enrichment</p>
                <p className="text-xs text-stone-400">Name-based professional footprint suggestion (Gemini). Off by default. Always human-confirmed.</p>
              </div>
            </div>
            <button
              onClick={() => setS((p) => ({ ...p, enrichment_enabled: !p.enrichment_enabled }))}
              data-testid="settings-enrichment-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${s.enrichment_enabled ? "bg-emerald-500" : "bg-stone-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${s.enrichment_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between border-t border-stone-100 pt-4">
            <div>
              <p className="text-sm font-semibold text-stone-800">Wipe &amp; re-run all enrichment</p>
              <p className="text-xs text-stone-400">Clears every lead's AI research and re-runs it from scratch with the latest conservative logic. Manually-confirmed leads are kept. Runs in the background.</p>
            </div>
            <button
              onClick={wipeRerun}
              disabled={rerunning}
              data-testid="settings-wipe-rerun"
              className="flex items-center gap-2 shrink-0 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60 rounded-md px-4 py-2 transition-colors"
            >
              {rerunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Wipe &amp; re-run
            </button>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4" data-testid="stores-card">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">Stores</p>
              <p className="text-xs text-stone-400">Add the physical showrooms (e.g. HSR, BTM). Each store-scoped user only sees leads assigned to their store and doesn&apos;t see AI features. <span className="font-semibold text-stone-700">Super Store</span> is the global admin scope and cannot be deleted.</p>
            </div>
          </div>
          <form onSubmit={addStore} className="flex gap-2">
            <input
              type="text"
              value={newStoreName}
              onChange={(e) => setNewStoreName(e.target.value)}
              placeholder="New store name (e.g. HSR)"
              data-testid="store-name-input"
              className="flex-1 border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60"
            />
            <button type="submit" disabled={storeBusy || !newStoreName.trim()} data-testid="store-add-button"
              className="bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              {storeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add store"}
            </button>
          </form>
          <div className="space-y-1.5">
            {stores.map((st) => (
              <div key={st.id} data-testid={`store-row-${st.id}`}
                className="flex items-center justify-between px-3 py-2 rounded-md border border-stone-200 bg-stone-50">
                <div className="flex items-center gap-2">
                  {st.system ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-stone-900 text-stone-100 font-medium tracking-wide uppercase">{st.name}</span>
                  ) : (
                    <span className="text-sm font-medium text-stone-800">{st.name}</span>
                  )}
                  {st.system && <span className="text-[11px] text-stone-400">global · sees all stores + AI</span>}
                </div>
                {!st.system && (
                  <button onClick={() => removeStore(st)} data-testid={`store-delete-${st.id}`}
                    className="text-xs text-stone-500 hover:text-red-600 transition-colors">
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4" data-testid="wati-owner-map-card">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">WATI owner → CRM owner mapping</p>
              <p className="text-xs text-stone-400">Match each WATI contact owner to a CRM user. On save, every lead with that WATI owner is assigned to the chosen user as its backend (calling) owner. New leads from WATI sync follow this mapping automatically.</p>
            </div>
          </div>

          {watiOwners.length === 0 ? (
            <p className="text-xs text-stone-400">No WATI owners found on leads yet.</p>
          ) : (
            <div className="space-y-2" data-testid="wati-owner-rows">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center px-1 pb-1 border-b border-stone-100">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400">WATI owner</span>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400 text-center">leads</span>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400">CRM backend owner</span>
              </div>
              {watiOwners.map((o) => (
                <div key={o.wati_owner} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center" data-testid={`wati-owner-row-${o.wati_owner}`}>
                  <span className="text-sm text-stone-800 truncate">{o.wati_owner}</span>
                  <span className="text-xs text-stone-500 tabular-nums text-center w-12">{o.lead_count}</span>
                  <select
                    value={ownerDraft[o.wati_owner] || ""}
                    onChange={(e) => setOwnerDraft((p) => ({ ...p, [o.wati_owner]: e.target.value }))}
                    data-testid={`wati-owner-select-${o.wati_owner}`}
                    className="w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                    <option value="">— Not mapped —</option>
                    {watiUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              ))}
              <div className="pt-2">
                <button onClick={applyOwnerMap} disabled={applyingMap} data-testid="apply-wati-owner-map"
                  className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
                  {applyingMap ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & apply mapping"}
                </button>
                <p className="text-xs text-stone-400 mt-2">Tip: if a WATI owner isn't in the list, create that person on the Users page first, then map them here.</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4" data-testid="requirements-card">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">Lead requirement options</p>
              <p className="text-xs text-stone-400">Configure the values that appear in the Requirement dropdown on every lead (Sofa / Bed / Recliner / …). Renames update every tagged lead automatically; deletes prompt you to choose where to migrate existing tags.</p>
            </div>
          </div>

          <div className="space-y-2" data-testid="requirements-rows">
            <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-1 pb-1 border-b border-stone-100">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400">Requirement</span>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400 text-center w-16">leads</span>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400 w-24 text-right">Actions</span>
            </div>

            {reqs.length === 0 && (
              <p className="text-xs text-stone-400 py-2">No requirements configured yet. Add one below.</p>
            )}

            {reqs.map((name) => {
              const isEditing = editingReq?.old === name;
              return (
                <div key={name} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center" data-testid={`requirement-row-${name}`}>
                  {isEditing ? (
                    <input autoFocus value={editingReq.new}
                      onChange={(e) => setEditingReq({ old: name, new: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditingReq(null); }}
                      data-testid={`requirement-edit-${name}`}
                      className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
                  ) : (
                    <span className="text-sm text-stone-800 truncate">{name}</span>
                  )}
                  <span className="text-xs text-stone-500 tabular-nums text-center w-16">{reqCounts[name] ?? 0}</span>
                  <div className="flex items-center justify-end gap-1 w-24">
                    {isEditing ? (
                      <>
                        <button onClick={saveRename} disabled={reqBusy} data-testid={`requirement-save-${name}`}
                          className="text-xs text-emerald-700 hover:bg-emerald-50 border border-emerald-200 rounded px-2 py-1 transition-colors disabled:opacity-60">Save</button>
                        <button onClick={() => setEditingReq(null)} data-testid={`requirement-cancel-${name}`}
                          className="text-xs text-stone-500 hover:bg-stone-100 border border-stone-200 rounded px-2 py-1 transition-colors">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditingReq({ old: name, new: name })} title="Rename"
                          data-testid={`requirement-edit-btn-${name}`}
                          className="p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setDeletingReq({ name, count: reqCounts[name] ?? 0, migrate_to: "" })}
                          title="Delete" data-testid={`requirement-delete-btn-${name}`}
                          className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="pt-3 flex items-center gap-2 border-t border-stone-100 mt-2">
              <input value={newReq} onChange={(e) => setNewReq(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addReq(); }}
                placeholder="Add a new requirement (e.g. Wardrobe)"
                data-testid="requirement-new-input"
                className="flex-1 border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
              <button onClick={addReq} disabled={reqBusy || !newReq.trim()}
                data-testid="requirement-add-btn"
                className="flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white text-sm px-3 py-1.5 rounded-md transition-colors disabled:opacity-60">
                {reqBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add
              </button>
            </div>
          </div>
        </div>

        {deletingReq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="requirement-delete-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">Delete &quot;{deletingReq.name}&quot;?</p>
                  <p className="text-sm text-stone-600 mt-1">
                    {deletingReq.count > 0
                      ? <><span className="font-medium text-stone-900">{deletingReq.count}</span> lead(s) are currently tagged with this requirement. Choose where to migrate them:</>
                      : "No leads are currently tagged with this requirement. It will be removed from the dropdown."}
                  </p>
                </div>
                <button onClick={() => setDeletingReq(null)} className="p-1 text-stone-400 hover:text-stone-700"><X className="h-4 w-4" /></button>
              </div>

              {deletingReq.count > 0 && (
                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Migrate tagged leads to</label>
                  <select value={deletingReq.migrate_to}
                    onChange={(e) => setDeletingReq((p) => ({ ...p, migrate_to: e.target.value }))}
                    data-testid="requirement-migrate-target"
                    className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                    <option value="">— Leave unset (clear the field) —</option>
                    {reqs.filter((r) => r !== deletingReq.name).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setDeletingReq(null)} data-testid="requirement-delete-cancel"
                  className="text-sm text-stone-600 hover:bg-stone-100 border border-stone-200 rounded-md px-3 py-1.5 transition-colors">Cancel</button>
                <button onClick={confirmDelete} disabled={reqBusy} data-testid="requirement-delete-confirm"
                  className="text-sm bg-red-600 hover:bg-red-700 text-white rounded-md px-3 py-1.5 transition-colors disabled:opacity-60 flex items-center gap-1.5">
                  {reqBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete &amp; migrate
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4" data-testid="bulk-store-card">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">Bulk reassign store for non-Fresh leads</p>
              <p className="text-xs text-stone-400">One-off migration. Sets every lead whose state is NOT &ldquo;Fresh&rdquo; to the chosen store. Preview always shows exact counts before you apply. Idempotent — safe to re-run.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">Target store</label>
              <select value={bulkTarget}
                onChange={(e) => { setBulkTarget(e.target.value); setBulkPreview(null); }}
                data-testid="bulk-store-target"
                className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                <option value="">— Choose a store —</option>
                {stores.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <button onClick={bulkPreviewRun} disabled={!bulkTarget || bulkBusy}
              data-testid="bulk-store-preview"
              className="inline-flex items-center gap-1.5 text-sm bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 rounded-md px-3 py-2 transition-colors disabled:opacity-60">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Preview counts
            </button>
          </div>

          {bulkPreview && (
            <div data-testid="bulk-store-preview-result" className="border border-stone-200 bg-stone-50 rounded-md p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-stone-500">Total non-Fresh leads</span><span className="font-medium tabular-nums text-stone-900">{bulkPreview.total_non_fresh}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Already on &ldquo;{bulkPreview.target_store}&rdquo;</span><span className="font-medium tabular-nums text-stone-900">{bulkPreview.already_on_target}</span></div>
              <div className="flex justify-between border-t border-stone-200 pt-1 mt-1"><span className="text-stone-700 font-medium">Will migrate</span><span className="font-semibold tabular-nums text-stone-900">{bulkPreview.would_migrate ?? bulkPreview.migrated ?? 0}</span></div>
              {bulkPreview.applied && (
                <p className="text-emerald-700 text-[11px] mt-1">✔ Applied on {new Date().toLocaleString()}</p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => setBulkConfirmOpen(true)}
              disabled={!bulkPreview || bulkPreview.would_migrate === 0 || bulkBusy || bulkPreview.applied}
              data-testid="bulk-store-apply-open"
              className="inline-flex items-center gap-1.5 text-sm bg-stone-900 hover:bg-stone-800 text-white rounded-md px-3 py-2 transition-colors disabled:opacity-40">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Store className="h-3.5 w-3.5" />}
              Apply migration
            </button>
          </div>
        </div>

        {bulkConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-store-confirm-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">Migrate {bulkPreview?.would_migrate ?? 0} lead(s) to &ldquo;{bulkTarget}&rdquo;?</p>
                  <p className="text-sm text-stone-600 mt-1">
                    Every lead not in the &ldquo;Fresh&rdquo; state will have its store set to <span className="font-medium text-stone-900">{bulkTarget}</span>. No other lead fields are touched. This cannot be automatically undone.
                  </p>
                </div>
                <button onClick={() => setBulkConfirmOpen(false)} className="p-1 text-stone-400 hover:text-stone-700"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setBulkConfirmOpen(false)} data-testid="bulk-store-confirm-cancel"
                  className="text-sm text-stone-600 hover:bg-stone-100 border border-stone-200 rounded-md px-3 py-1.5 transition-colors">Cancel</button>
                <button onClick={bulkApply} disabled={bulkBusy} data-testid="bulk-store-confirm-apply"
                  className="text-sm bg-stone-900 hover:bg-stone-800 text-white rounded-md px-3 py-1.5 transition-colors disabled:opacity-60 flex items-center gap-1.5">
                  {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Store className="h-3.5 w-3.5" />}
                  Yes, migrate
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5" data-testid="backup-restore-card">
          <div className="flex items-center gap-2">
            <HardDriveDownload className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">Full database backup &amp; restore</p>
              <p className="text-xs text-stone-400">Download the entire CRM (leads, users, activity history &amp; settings) as one Excel file. Re-upload it later to roll back to that exact state.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={downloadFullBackup} disabled={downloading} data-testid="download-full-backup"
              className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download full backup (.xlsx)
            </button>
            <label data-testid="choose-restore-file"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-4 py-2 rounded-md transition-colors cursor-pointer">
              <Upload className="h-4 w-4" /> Choose backup to restore…
              <input type="file" accept=".xlsx" className="hidden" data-testid="restore-file-input"
                onChange={(e) => { setRestoreFile(e.target.files?.[0] || null); e.target.value = ""; }} />
            </label>
          </div>

          {restoreFile && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3" data-testid="restore-confirm-panel">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700 leading-relaxed">
                  <strong>This will replace ALL current data</strong> with the contents of
                  <span className="font-mono"> {restoreFile.name}</span>. Any leads, users or history created
                  after this backup will be permanently removed. Download a fresh backup first if unsure.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={doRestore} disabled={restoring} data-testid="confirm-restore-button"
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
                  {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, restore & replace everything"}
                </button>
                <button onClick={() => setRestoreFile(null)} disabled={restoring} data-testid="cancel-restore-button"
                  className="text-sm text-stone-500 hover:text-stone-700 px-2">Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4" data-testid="import-leads-card">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-semibold text-stone-800">Import leads (CSV / Excel)</p>
              <p className="text-xs text-stone-400">Bulk-add new leads from a spreadsheet. Required columns: <span className="font-mono">name, phone</span>. Optional: source, lead_type, city, state, assigned_to_email, next_action_date, note. Duplicate phone numbers are skipped.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={downloadTemplate} data-testid="download-import-template"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-4 py-2 rounded-md transition-colors">
              <Download className="h-4 w-4" /> Download template
            </button>
            <label data-testid="import-leads-label"
              className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload leads file
              <input type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-leads-input"
                disabled={importing} onChange={onImportFile} />
            </label>
          </div>
          {importResult && (
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600 space-y-1" data-testid="import-result">
              <p><span className="font-semibold text-emerald-700">{importResult.created}</span> created · <span className="font-semibold text-amber-700">{importResult.skipped_duplicates}</span> skipped (duplicate phone)</p>
              {importResult.errors?.length > 0 && (
                <ul className="list-disc pl-5 text-red-600">
                  {importResult.errors.slice(0, 8).map((er, i) => <li key={i}>{er}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-stone-400 leading-relaxed">
          DPDP: enquiry data is used only to qualify and route leads. We never perform phone reverse-identity lookups or social scraping.
        </p>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * EmailHealthCard — shows the outcome of the most recent OTP email
 * dispatch. Detects three states:
 *   1. Not configured — RESEND_API_KEY absent, codes are being logged
 *      to the backend console instead of emailed.
 *   2. Failing — last attempt raised an error (bad key, unverified
 *      recipient/domain, quota, etc). Displays the exact error text
 *      so the admin doesn't have to dig into logs.
 *   3. Healthy — last email was delivered to Resend successfully.
 * ---------------------------------------------------------------- */
function EmailHealthCard({ health, onRefresh }) {
  if (!health) return null;

  const configured = !!health.resend_configured;
  const otpOn = !!health.otp_enabled;
  const status = health.last_status || "never";
  const lastAt = health.last_attempt_at
    ? new Date(health.last_attempt_at).toLocaleString()
    : null;

  let tone = "ok";      // ok | warn | fail | idle
  let heading = "Email delivery — healthy";
  let body = null;
  let Icon = MailCheck;

  if (!otpOn) {
    tone = "warn";
    heading = "OTP is disabled";
    body = "Sign-in codes are turned off (OTP_ENABLED=false). Users are logging in with password only.";
    Icon = MailWarning;
  } else if (!configured) {
    tone = "warn";
    heading = "Resend not configured";
    body = "RESEND_API_KEY is missing. Sign-in codes are printed to the server log instead of being emailed. Add the env var and redeploy to fix.";
    Icon = MailWarning;
  } else if (status === "failed") {
    tone = "fail";
    heading = "Last email failed to send";
    body = health.last_error || "Unknown error from email provider.";
    Icon = MailWarning;
  } else if (status === "sent") {
    tone = "ok";
    heading = "Email delivery — healthy";
    body = `Last code delivered to ${health.last_to} · sender ${health.last_sender}`;
    Icon = MailCheck;
  } else {
    tone = "idle";
    heading = "No email sent yet";
    body = "The next successful sign-in will send a code and update this status.";
    Icon = MailCheck;
  }

  const toneClasses = {
    ok:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    fail: "border-red-200 bg-red-50 text-red-900",
    idle: "border-stone-200 bg-stone-50 text-stone-700",
  }[tone];

  const iconClasses = {
    ok:   "text-emerald-600",
    warn: "text-amber-600",
    fail: "text-red-600",
    idle: "text-stone-500",
  }[tone];

  return (
    <div
      className={`border rounded-xl p-5 ${toneClasses}`}
      data-testid="email-health-card"
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${iconClasses}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold" data-testid="email-health-heading">{heading}</p>
            <button
              onClick={onRefresh}
              className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1"
              data-testid="email-health-refresh"
              title="Refresh"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
          {body && (
            <p className="text-xs mt-1 leading-relaxed break-words" data-testid="email-health-body">
              {body}
            </p>
          )}
          {lastAt && (
            <p className="text-[11px] opacity-70 mt-2">
              Last attempt: {lastAt}
              {health.last_provider_id ? ` · Resend id ${health.last_provider_id.slice(0, 8)}…` : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
